import { useState } from "react";
import { useTranslation } from "react-i18next";
import { talents } from "@/mocks/talents";

/* ─── Engagement ring ─────────────────────────────── */
function EngagementRing({ value, active }: { value: string; active: boolean }) {
  const { t } = useTranslation();
  const num = parseFloat(value);
  const radius = 28;
  const circ = 2 * Math.PI * radius;
  const progress = active ? (num / 15) * circ : 0;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-16 h-16 flex items-center justify-center">
        <svg width="64" height="64" className="-rotate-90">
          <circle cx="32" cy="32" r={radius} fill="none" stroke="rgba(208,248,85,0.15)" strokeWidth="3" />
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="#d0f855"
            strokeWidth="3"
            strokeDasharray={circ}
            strokeDashoffset={circ - progress}
            strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 1.2s ease" }}
          />
        </svg>
        <span className="absolute text-xs font-black" style={{ color: "#d0f855" }}>
          {value}
        </span>
      </div>
      <span className="text-xs uppercase tracking-widest opacity-50 text-white text-center">
        {t("talentos.engagement")}
      </span>
    </div>
  );
}

/* ─── Single talent slide ─────────────────────────── */
interface TalentSlideProps {
  talent: (typeof talents)[0];
  index: number;
}

function TalentSlide({ talent, index }: TalentSlideProps) {
  const { t, i18n } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const isEven = index % 2 === 0; // even → photo left, info right | odd → info left, photo right
  const bio = i18n.language?.startsWith("en") && talent.bioEn ? talent.bioEn : talent.bio;

  const MobileLabel = (
    <div className="lg:hidden flex flex-col px-6 pt-8 pb-5">
      <h3
        className="font-black text-3xl leading-none text-white"
        style={{ letterSpacing: "-0.02em" }}
      >
        {talent.name}
      </h3>
      <p
        className="mt-3 text-xs uppercase tracking-[0.2em] font-semibold"
        style={{ color: "#d0f855" }}
      >
        {talent.category}
      </p>
    </div>
  );

  const PhotoCol = (
    <div
      className="relative overflow-hidden flex-shrink-0 w-full h-[115vw] max-h-[560px] sm:h-[70vw] lg:h-auto lg:min-h-[600px] lg:w-[42%]"
    >
      <img
        src={talent.photos[0]}
        alt={talent.name}
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          objectPosition: talent.photoPosition ?? "center 15%",
          transform: `translateX(${talent.photoShiftX ?? 0}%) scale(${talent.photoZoom ?? 1})`,
        }}
      />
      {/* gradient on inner edge — wide and strong to avoid white line */}
      <div
        className="absolute inset-y-0 z-10 pointer-events-none hidden lg:block"
        style={{
          [isEven ? "right" : "left"]: 0,
          width: "55%",
          background: isEven
            ? "linear-gradient(to right, transparent 0%, #0d0b1e 80%)"
            : "linear-gradient(to left, transparent 0%, #0d0b1e 80%)",
        }}
      />
      {/* Also cover the opposite edge so no bleed */}
      <div
        className="absolute inset-y-0 z-10 pointer-events-none hidden lg:block"
        style={{
          [isEven ? "left" : "right"]: 0,
          width: "6%",
          background: isEven
            ? "linear-gradient(to left, transparent, #0d0b1e)"
            : "linear-gradient(to right, transparent, #0d0b1e)",
        }}
      />
    </div>
  );

  const SOCIAL_ICONS: Record<string, string> = {
    instagram: "ri-instagram-line",
    youtube: "ri-youtube-line",
    tiktok: "ri-tiktok-line",
    facebook: "ri-facebook-line",
    twitter: "ri-twitter-x-line",
    linkedin: "ri-linkedin-line",
    twitch: "ri-twitch-line",
  };
  const SOCIAL_COLORS: Record<string, string> = {
    instagram: "#E1306C",
    youtube: "#FF0000",
    tiktok: "#d0f855",
    facebook: "#1877F2",
    twitter: "#e7e7e7",
    linkedin: "#0A66C2",
    twitch: "#9146FF",
  };

  const InfoCol = (
    <div className="flex flex-col sm:flex-row items-stretch w-full lg:w-[58%]">
      {/* ── LEFT: category + name + bio + stats + tags ── */}
      <div className="flex flex-col justify-center px-6 sm:px-8 lg:px-14 py-8 md:py-12" style={{ flex: "1 1 0", minWidth: 0 }}>
        <p
          className="hidden lg:block text-sm uppercase tracking-[0.2em] font-semibold mb-4"
          style={{ color: "#d0f855" }}
        >
          {talent.category}
        </p>

        <h3
          className="hidden lg:block font-black text-4xl sm:text-5xl lg:text-6xl leading-none mb-4 md:mb-5 text-white"
          style={{ letterSpacing: "-0.02em" }}
        >
          {talent.name}
        </h3>

        <p className="text-base leading-relaxed mb-8" style={{ color: "rgba(255,255,255,0.6)" }}>
          {bio}
        </p>

        <div className="flex items-center gap-10 mb-8">
          <div>
            <span className="block text-3xl sm:text-4xl lg:text-5xl font-black" style={{ color: "#d0f855" }}>
              {talent.totalFollowers}
            </span>
            <span className="text-sm uppercase tracking-widest opacity-50 text-white">
              {t("talentos.followers")}
            </span>
          </div>
          <EngagementRing value={talent.engagement} active={hovered} />
        </div>

        <div className="flex flex-wrap gap-2">
          {talent.tags.map((tag) => (
            <span
              key={tag}
              className="px-4 py-1.5 rounded-full text-sm font-semibold"
              style={{ backgroundColor: "rgba(208,248,85,0.12)", color: "#d0f855", border: "1px solid rgba(208,248,85,0.25)" }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* ── RIGHT: social networks — 50% of InfoCol ── */}
      <div
        className="flex flex-col justify-center py-6 md:py-12 px-6 sm:px-8 gap-3 md:gap-4 flex-1 min-w-0 border-t sm:border-t-0 sm:border-l border-white/[0.07]"
      >
        <p
          className="text-sm uppercase tracking-[0.2em] font-semibold mb-1"
          style={{ color: "rgba(255,255,255,0.35)" }}
        >
          {t("talentos.socials")}
        </p>
        <div className="flex flex-row sm:flex-col gap-2 sm:gap-4 justify-center sm:justify-start">
          {talent.socials.map((s) => (
            <a
              key={s.platform}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-5 group cursor-pointer rounded-xl px-1 py-2 sm:px-5 sm:py-4 transition-all hover:scale-[1.02] flex-1 basis-0 min-w-0 max-w-[88px] sm:max-w-none aspect-square sm:aspect-auto"
              style={{
                backgroundColor: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <div
                className="w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              >
                <i
                  className={`${SOCIAL_ICONS[s.platform]} text-lg sm:text-2xl`}
                  style={{ color: SOCIAL_COLORS[s.platform] }}
                />
              </div>
              <div className="flex flex-col items-center sm:items-start min-w-0">
                <span className="text-xs sm:text-xl font-black text-white leading-tight">{s.followers}</span>
                <span className="hidden sm:block text-sm capitalize" style={{ color: "rgba(255,255,255,0.45)" }}>{s.platform}</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="relative w-full flex items-stretch overflow-hidden cursor-default"
      style={{
        backgroundColor: "#0d0b1e",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        minHeight: "600px",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Layout: alternating */}
      <div className={`relative z-10 w-full flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"}`}>
        {MobileLabel}
        {PhotoCol}
        {InfoCol}
      </div>
    </div>
  );
}

/* ─── Section ─────────────────────────────────────── */
export default function TalentosSection() {
  const { t } = useTranslation();
  return (
    <section id="talentos" className="relative w-full" style={{ backgroundColor: "#0d0b1e" }}>
      {/* Section header */}
      <div className="w-full pt-20 pb-12 px-6 md:px-16 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: "#d0f855" }}>
            {t("talentos.tag")}
          </p>
          <h2
            className="font-black text-4xl md:text-6xl lg:text-7xl leading-[1.05] md:leading-none text-white"
            style={{ letterSpacing: "-0.03em" }}
          >
            {t("talentos.title1")}{" "}<span style={{ color: "#d0f855" }}>{t("talentos.title2")}</span>
          </h2>
        </div>
      </div>

      {/* Thin separator between header and first talent (mobile) */}
      <div className="lg:hidden px-6">
        <div className="w-full border-t border-white/[0.07]" />
      </div>

      {/* Talent slides */}
      <div className="w-full">
        {talents.map((talent, i) => (
          <TalentSlide key={talent.id} talent={talent} index={i} />
        ))}
      </div>
    </section>
  );
}
